import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Info, ArrowRight, Check } from "lucide-react";
import { PORTFOLIO } from "@/data/content";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.06 } }),
};

const ShowcaseImage = ({ url, label, testId }) => (
  <motion.figure
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, margin: "-60px" }}
    variants={fadeUp}
    data-testid={testId}
    className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm"
  >
    <img src={url} alt={label} loading="lazy" className="w-full h-auto block" />
    <figcaption className="px-5 py-3 text-sm font-semibold text-slate-600 border-t border-slate-100">{label}</figcaption>
  </motion.figure>
);

export default function PortfolioPage() {
  const { project, disclaimer } = PORTFOLIO;

  return (
    <div data-testid="portfolio-page">
      {/* Header */}
      <section className="bg-slate-50 py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm font-semibold text-green-600 uppercase tracking-wider mb-3">Portfolio</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-slate-900">Nos réalisations</h1>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
            Un aperçu de ce que nous créons pour les commerces et artisans : identité de marque,
            sites web et supports imprimés cohérents et professionnels.
          </p>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8">
        <div data-testid="portfolio-disclaimer" className="flex items-start gap-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 p-4 sm:p-5">
          <Info size={20} className="shrink-0 mt-0.5" />
          <p className="text-sm sm:text-base leading-relaxed">{disclaimer}</p>
        </div>
      </section>

      {/* Project */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <span className="inline-block text-xs font-bold uppercase tracking-wider text-green-700 bg-green-50 rounded-full px-3 py-1 mb-4">{project.type}</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">{project.name}</h2>
            <p className="text-lg text-green-600 font-semibold italic mt-1">{project.subtitle}</p>
            <p className="mt-5 text-slate-600 leading-relaxed text-lg">{project.description}</p>
            <div className="flex flex-wrap gap-2 mt-6">
              {project.tags.map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-700 bg-slate-100 rounded-full px-3 py-1.5">
                  <Check size={14} className="text-green-600" /> {t}
                </span>
              ))}
            </div>
          </div>

          {/* Website */}
          <div className="mb-16">
            <h3 className="font-heading text-2xl font-bold text-slate-900 mb-2">Le site web</h3>
            <p className="text-slate-500 mb-8">Un site vitrine moderne, chaleureux et pensé pour la réservation.</p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
              {project.website.map((img, i) => (
                <ShowcaseImage key={i} url={img.url} label={img.label} testId={`portfolio-website-${i}`} />
              ))}
            </div>
          </div>

          {/* Print */}
          <div>
            <h3 className="font-heading text-2xl font-bold text-slate-900 mb-2">Les supports imprimés</h3>
            <p className="text-slate-500 mb-8">Une identité cohérente jusque sur le papier.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
              {project.print.map((img, i) => (
                <ShowcaseImage key={i} url={img.url} label={img.label} testId={`portfolio-print-${i}`} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-slate-900 py-16 lg:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white">Envie du même niveau pour votre commerce ?</h2>
          <p className="text-slate-300 mt-4 text-lg">Parlons de votre projet — logo, site, supports, on s'occupe de tout.</p>
          <Link
            to="/contact"
            data-testid="portfolio-cta"
            className="mt-8 inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white rounded-xl px-7 py-3.5 font-semibold text-lg transition-colors shadow-sm"
          >
            Demander un devis <ArrowRight size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
}
